import {usePropertyPanelContext} from '@/features/remotion-editor/editor/contexts/property-panel-context';
import {useWriteContext} from '@/features/remotion-editor/editor/utils/use-context';
import {SceneNarrationPreview} from '@waka/compat/SceneNarrationPreview';
import {SourceScriptPanel} from '@waka/compat/SourceScriptPanel';
import {contentScenes,sceneCaptions,sceneCaptionText} from '@waka/compat/source-script-native';
import {useFullState} from '@/features/remotion-editor/editor/utils/use-context';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { PlayerRef } from '@remotion/player';
import {
  useAllItems,
  useAssets,
  useFps,
  useTracks,
} from '@/features/remotion-editor/editor/utils/use-context';
import { useTimelinePosition } from '@/features/remotion-editor/editor/utils/use-timeline-position';
import { convertMsToFrame } from '@/features/remotion-editor/editor/utils/convert-ms-to-frame';
import { renderTimesMs } from '@/features/remotion-editor/editor/utils/render-times-ms';
import ScriptPropertyPanelContentDivider from '@/features/remotion-editor/editor/editor-property-panel/ui/common/ScriptPropertyPanelContentDivider';
import { isCaptionsItem } from '@/features/remotion-editor/editor/items/captions/captions-item-type';
import {
  AudioAsset,
  isCaptionAsset,
} from '@/features/remotion-editor/editor/assets/assets';
import ScriptContentSceneCard from '@/features/remotion-editor/editor/editor-property-panel/ui/contents/script/ScriptContentSceneCard';
import { useGetTTSVoicesApi } from '@/shared/apis/tts/get-tts-voices';
import { DtoEmotion, DtoVendor } from '@/shared/constants';
import { useParams, useSearchParams } from 'next/navigation';
import { useEditorStateConsoleLog } from '@/features/remotion-editor/editor/debuging-console/editor-state-console';
import { convertFrameToMs } from '@/features/remotion-editor/editor/utils/convert-frame-to-ms';
import {
  captionScriptText,
  captionWindowText,
} from '@waka/compat/caption-script';
import { scriptSceneKey,findScriptSceneCard } from '@waka/compat/script-scene-key';

interface ScriptContentProps {
  playerRef: React.RefObject<PlayerRef | null>;
}

import {scriptSceneVoiceSettings} from '@waka/compat/script-scene-voice-settings';

type ScriptSceneClip = {
  itemId: string;
  sceneId: number | null;
  linkId: number | null;
  videoStart: number;
  videoEnd: number;
  combinedText: string;
};

export type ScriptSceneGroup = {
  sceneId: number | null;
  sceneKey: string;
  clips: ScriptSceneClip[];
  combinedText: string;
  videoStart: number;
  videoEnd: number;
  voiceId?: string | null;
  vendorModel?: DtoVendor | null;
  pitch?: number | null;
  emotion?: string | null;
  speedFactor?: number | null;
  style?: string;
  audioAsset?: {
    src: string | null;
  };
};

const safeMs = (value?: number | null) =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

function ScriptContent({ playerRef }: ScriptContentProps) {
  const t = useTranslations();
  const fullState=useFullState(),{setState}=useWriteContext(),{setActiveMenu}=usePropertyPanelContext();

  const params = useParams<{ projectId: string }>();
  const searchParams = useSearchParams();

  const projectId = params?.projectId;
  const storyId = searchParams?.get('storyId') as string | undefined;

  const { tracks } = useTracks();
  const { items } = useAllItems();
  const { assets } = useAssets();
  const { fps } = useFps();
  const currentFrame = useTimelinePosition({ playerRef });
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const lastScrolledCardIdRef = useRef<string | null>(null);

  const [isTtsGenerationLoading, setIsTtsGenerationLoading] = React.useState();

  const { data: voiceData } = useGetTTSVoicesApi();

  const handleSceneSeek = useCallback(
    (targetVideoStartMs?: number) => {
      if (
        !playerRef.current ||
        fps <= 0 ||
        typeof targetVideoStartMs !== 'number' ||
        !Number.isFinite(targetVideoStartMs)
      ) {
        return;
      }

      const targetFrame = convertMsToFrame(targetVideoStartMs, 'start', fps);
      // +1 한 이유 ms, frame등 변환할 때 작은 오차범위로도 가라오케 표시가 안되는 경우가 있어서 임시 방편으로 넣음
      playerRef.current.seekTo(targetFrame + 1);
    },
    [fps, playerRef],
  );

  const scriptScenes = useMemo<ScriptSceneGroup[]>(() => {
    if (!tracks?.length) {
      return [];
    }

    const captionTracks = tracks.filter(
      (track) =>
        track.metadata?.type === 'caption' && track.metadata?.isMainTrack,
    );

    if (!captionTracks.length) {
      return [];
    }

    const audioTrack = tracks.find(
      (track) =>
        track.metadata?.type === 'audio' && track.metadata?.isMainTrack,
    );
    const audioBySceneId = new Map<
      number,
      {
        asset: AudioAsset;
        voiceId?: string | null;
        vendorModel?: DtoVendor | null;
        pitch?: number | null;
        emotion?: DtoEmotion | null;
        speedFactor?: number | null;
      }
    >();

    audioTrack?.items.forEach((audioItemId) => {
      const item = items[audioItemId];
      if (item?.type !== 'audio') {
        return;
      }

      const sceneId = item.renderMeta?.sceneId;
      const voiceId = item.renderMeta?.voiceId;
      const vendorModel = item.renderMeta?.vendorModel;
      const pitch = item.renderMeta?.pitch;
      const emotion = item.renderMeta?.emotion;
      const speedFactor = item.renderMeta?.speedFactor;
      const asset = assets[item.assetId];
      if (sceneId == null || !asset || asset.type !== 'audio') {
        return;
      }

      if (!audioBySceneId.has(sceneId)) {
        audioBySceneId.set(sceneId, {
          asset,
          voiceId,
          vendorModel,
          pitch,
          emotion,
          speedFactor,
        });
      }
    });

    type SceneAccumulator = {
      sceneId: number | null;
      sceneKey: string;
      clips: ScriptSceneClip[];
      videoStart: number;
      videoEnd: number;
    };

    const groupedScenes: SceneAccumulator[] = [];
    const sceneIndexMap = new Map<string, number>();

    captionTracks
      .flatMap((track) => track.items)
      .sort((a, b) => (items[a]?.from ?? 0) - (items[b]?.from ?? 0))
      .forEach((itemId) => {
        const candidate = items[itemId];
        if (!isCaptionsItem(candidate)) {
          return;
        }

        const asset = assets[candidate.assetId];
        if (!isCaptionAsset(asset)) {
          return;
        }

        const sceneId = Number.isFinite(candidate.renderMeta?.sceneId) ? candidate.renderMeta!.sceneId : Number.isFinite(asset.sceneId) ? asset.sceneId : null;
        const sceneKey = scriptSceneKey(candidate, sceneId);

        if (!sceneIndexMap.has(sceneKey)) {
          const sceneKeyIndex = groupedScenes.length;
          sceneIndexMap.set(sceneKey, sceneKeyIndex);
          groupedScenes.push({
            sceneId: sceneId != null ? sceneId : null,
            sceneKey,
            clips: [],
            videoStart: Number.POSITIVE_INFINITY,
            videoEnd: 0,
          });
        }

        const sceneIndex = sceneIndexMap.get(sceneKey);
        if (sceneIndex === undefined) {
          return;
        }

        const videoStart = safeMs(convertFrameToMs(candidate.from, fps));
        const videoEnd = safeMs(
          convertFrameToMs(candidate.from + candidate.durationInFrames, fps),
        );

        const linkId = Number.isFinite(asset.linkId) ? asset.linkId : null;
        const combinedText = captionWindowText(candidate, asset, fps);

        const clip: ScriptSceneClip = {
          itemId,
          sceneId: sceneId != null ? sceneId : null,
          linkId: linkId != null ? linkId : null,
          videoStart,
          videoEnd,
          combinedText,
        };

        const currentScene = groupedScenes[sceneIndex];

        if (currentScene) {
          currentScene.clips.push(clip);
          currentScene.videoStart = Math.min(
            currentScene.videoStart,
            videoStart,
          );
          currentScene.videoEnd = Math.max(currentScene.videoEnd, videoEnd);
        }
      });

    const fallbackScenes=groupedScenes.map((scene) => {
      const combinedText = captionScriptText(
        scene.clips.flatMap((clip) => {
          const item = items[clip.itemId];
          if (!isCaptionsItem(item)) return [];
          const asset = assets[item.assetId];
          if (!isCaptionAsset(asset)) return [];
          return [
            {
              item,
              asset,
              trackId: tracks.find((track) => track.items.includes(item.id))
                ?.id,
            },
          ];
        }),
        fps,
      );
      const audioEntry =
        scene.sceneId != null ? audioBySceneId.get(scene.sceneId) : undefined;
      const nativeVoice=scriptSceneVoiceSettings(items,assets,scene.sceneKey,scene.clips.map(clip=>clip.itemId),convertMsToFrame(scene.videoStart,'start',fps),convertMsToFrame(scene.videoEnd,'end',fps),tracks);

      return {
        sceneId: scene.sceneId,
        sceneKey: scene.sceneKey,
        clips: scene.clips,
        combinedText,
        videoStart: Number.isFinite(scene.videoStart) ? scene.videoStart : 0,
        videoEnd: scene.videoEnd,
        voiceId: nativeVoice?.voiceId ?? audioEntry?.voiceId ?? null,
        vendorModel: audioEntry?.vendorModel ?? null,
        pitch: nativeVoice?.pitch ?? audioEntry?.pitch ?? null,
        emotion: nativeVoice?.emotion ?? audioEntry?.emotion ?? null,
        speedFactor: nativeVoice?.speedFactor ?? audioEntry?.speedFactor ?? null,
        style: nativeVoice?.style,
        audioAsset: nativeVoice?{src:nativeVoice.src}:audioEntry
          ? {
              src: audioEntry.asset.remoteUrl,
            }
          : undefined,
      } satisfies ScriptSceneGroup;
    });
    const semantic=contentScenes(fullState.undoableState);
    if(!semantic.length)return fallbackScenes;
    const nativeScenes=semantic.map(scene=>{
      const entries=sceneCaptions(fullState.undoableState,scene),existing=fallbackScenes.find(old=>old.clips.some(clip=>scene.members.includes(clip.itemId)));
      return {...existing,sceneId:null,sceneKey:scene.key,clips:entries.map(({item,asset})=>({itemId:item.id,sceneId:null,linkId:null,videoStart:item.from/fps*1000,videoEnd:(item.from+item.durationInFrames)/fps*1000,combinedText:captionWindowText(item,asset,fps)})),combinedText:sceneCaptionText(fullState.undoableState,scene),videoStart:scene.from/fps*1000,videoEnd:scene.end/fps*1000} satisfies ScriptSceneGroup;
    });
    return [...nativeScenes,...fallbackScenes.filter(scene=>scene.clips.every(clip=>!items[clip.itemId]?.sourceScene))];
  }, [assets, fps, items, tracks,fullState.undoableState]);

  const activeSceneKey = useMemo(() => {
    for (const scene of scriptScenes) {
      const startFrame = convertMsToFrame(scene.videoStart, 'start', fps);
      let endFrame = convertMsToFrame(scene.videoEnd, 'end', fps);

      if (endFrame <= startFrame) {
        endFrame = startFrame + 1;
      }

      if (startFrame <= currentFrame && currentFrame < endFrame) {
        return scene.sceneKey;
      }
    }

    return null;
  }, [currentFrame, fps, scriptScenes]);

  useEffect(() => {
    const sectionElement = sectionRef.current;
    if (!sectionElement || !activeSceneKey) {
      lastScrolledCardIdRef.current = null;
      return;
    }

    const activeElement = findScriptSceneCard(sectionElement,activeSceneKey);

    if (!activeElement) {
      lastScrolledCardIdRef.current = null;
      return;
    }

    if (lastScrolledCardIdRef.current === activeSceneKey) {
      return;
    }

    lastScrolledCardIdRef.current = activeSceneKey;
    activeElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [activeSceneKey]);

  const hasClips = scriptScenes.some((scene) => scene.clips.length > 0);

  // console.log(scriptScenes, ' - scriptScenes');

  useEditorStateConsoleLog();
  return (
    <div className="absolute top-0 left-0 flex h-full w-full flex-col gap-2xs px-md py-sm">
      <section
        ref={sectionRef}
        className="no-scrollbar flex-1 overflow-y-auto layout-padding-bottom"
      >
        {contentScenes(fullState.undoableState).length>0&&<SourceScriptPanel voices={voiceData?.data}/>}
        {!hasClips && (
          <p className="text-body-medium text-sys-cont-neutral-light">
            {t('Editor.script-panel.empty')}
          </p>
        )}

        {hasClips && (
          <div className="py-3xs">
            <ScriptPropertyPanelContentDivider dashStyle={'border-solid'} />
          </div>
        )}

        {scriptScenes.map((scene, sceneIndex) => {
          if (!scene.clips.length&&!contentScenes(fullState.undoableState).some(source=>source.key===scene.sceneKey))return null;

          const sceneLabel = t('Editor.script-panel.scene-label', {
            sceneId: scene.sceneId ?? (sceneIndex + 1),
          });

          const semantic=contentScenes(fullState.undoableState).find(source=>source.key===scene.sceneKey);
          if(semantic)return <div key={scene.sceneKey} data-script-card-id={scene.sceneKey} className="grid gap-2xs py-3xs">
            <button type="button" onClick={()=>handleSceneSeek(scene.videoStart)}>{sceneLabel} · {renderTimesMs(scene.videoStart)}–{renderTimesMs(scene.videoEnd)}</button>
            <p className="text-xs">화면 자막 · 읽을 나레이션은 위 원본 대본에서 저장합니다.</p>
            <p style={{whiteSpace:'pre-wrap'}}>{scene.combinedText||'화면 자막이 없습니다.'}</p>
            <button type="button" onClick={()=>{setState({commitToUndoStack:false,update:state=>({...state,selectedItems:scene.clips.map(clip=>clip.itemId)})});setActiveMenu('caption');}}>자막 탭에서 줄·단어 편집</button>
            <SceneNarrationPreview start={semantic.from} end={semantic.end} playerRef={playerRef}/>
            <ScriptPropertyPanelContentDivider dashStyle="border-solid"/>
          </div>;
          return (
            <div key={scene.sceneKey}>
              <ScriptContentSceneCard
                playerRef={playerRef}
                key={scene.sceneKey}
                projectId={projectId}
                storyId={storyId}
                cardId={scene.sceneKey}
                sceneLabel={sceneLabel}
                isActive={scene.sceneKey === activeSceneKey}
                timelineLabel={`${renderTimesMs(scene.videoStart)}-${renderTimesMs(scene.videoEnd)}`}
                initialText={scene.combinedText}
                voiceId={scene.voiceId ?? null}
                voiceData={voiceData?.data}
                audioSrc={scene.audioAsset?.src ?? null}
                onSeekToScene={() => handleSceneSeek(scene.videoStart)}
                scene={scene}
              />
              <div className="py-3xs">
                <ScriptPropertyPanelContentDivider dashStyle={'border-solid'} />
              </div>
            </div>
          );
        })}
      </section>
    </div>
  );
}

export default ScriptContent;
